import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom'
import { useGeoUnitProfile } from '../../hooks/useGeoAnalytics'
import type { GeoUnitTypeKey, TurnoutPoint } from '../../types/geoAnalytics'
import { formatGeoUnitLabel } from './geoUnitLabel'
import { LeanExplainer } from './LeanExplainer'
import { StatTile, vizSeqVar } from '../shared/viz'

export type UnitAction = 'export' | 'apply-tags' | 'create-turf'

// One vocabulary for the two ways a metric can be blank, so the scorecard never mixes
// "no lean data" / "insufficient" / a vanished section for the same underlying condition.
const NO_DATA = 'No data (not available for this geography)'
const INSUFFICIENT = 'Insufficient data (fewer than 10 voters)'

const FULL_PROFILE_UNIT_TYPES: GeoUnitTypeKey[] = [
  'congressionalDistrict',
  'stateSenateDistrict',
  'stateHouseDistrict',
  'county',
]

interface UnitScorecardProps {
  unitId: string
  onClose: () => void
  /** Omit on mobile — bulk actions stay desktop-only per the analytics page posture. */
  onAction?: (action: UnitAction, boundaryWkt: string) => void
}

export function UnitScorecard({ unitId, onClose, onAction }: UnitScorecardProps) {
  const { data: profile, isLoading, error } = useGeoUnitProfile(unitId)

  return (
    <Box bg="surface" borderRadius="md" boxShadow="lg" p={3} w="full">
      <Flex justify="space-between" align="flex-start" mb={1}>
        <Box>
          {isLoading ? (
            <Skeleton h="20px" w="140px" />
          ) : (
            <Text fontWeight="bold" fontSize="sm" lineClamp={1}>
              {profile
                ? formatGeoUnitLabel(profile.unitType, profile.code, profile.name)
                : 'Unknown area'}
            </Text>
          )}
          {profile && (
            <Text fontSize="xs" color="gray.500">{profile.code}</Text>
          )}
        </Box>
        <CloseButton size="sm" onClick={onClose} aria-label="Close scorecard" />
      </Flex>

      {!!error && (
        <Text fontSize="sm" color="red.500">Could not load area profile.</Text>
      )}

      {profile && (
        <VStack align="stretch" gap={2}>
          <HStack gap={2} flexWrap="wrap">
            {profile.leanFormatted ? (
              <Badge
                colorPalette={profile.leanFormatted.startsWith('D') ? 'blue' : profile.leanFormatted.startsWith('R') ? 'red' : 'gray'}
                fontSize="sm"
              >
                {profile.leanFormatted}
              </Badge>
            ) : (
              <Badge colorPalette="gray" fontSize="sm">No lean data</Badge>
            )}
            {profile.leanByCycle.map(c => (
              <Text key={c.year} fontSize="xs" color="gray.500">
                {c.year}: {c.lean > 0 ? `D+${c.lean.toFixed(1)}` : c.lean < 0 ? `R+${Math.abs(c.lean).toFixed(1)}` : 'EVEN'}
              </Text>
            ))}
            {profile.leanFormatted && (
              <LeanExplainer
                leanFormatted={profile.leanFormatted}
                leanByCycle={profile.leanByCycle}
              />
            )}
          </HStack>

          <Box>
            <Text fontSize="xs" color="gray.500" mb={0.5}>Turnout (general)</Text>
            {profile.turnout.length === 0 ? (
              <Text fontSize="xs" color="gray.500">{NO_DATA}</Text>
            ) : (
              <Flex align="flex-end" gap={2}>
                <TurnoutSparkline points={profile.turnout} />
                <VStack align="flex-start" gap={0}>
                  {profile.turnout.map(t => (
                    <Text key={t.year} fontSize="xs" color="gray.600">
                      {t.year}: {t.suppressed ? INSUFFICIENT : t.value != null ? `${t.value.toFixed(1)}%` : NO_DATA}
                    </Text>
                  ))}
                </VStack>
              </Flex>
            )}
          </Box>

          {profile.dropoff.map(d => (
            <StatTile
              key={d.targetYear}
              label={`Midterm drop-off (${d.targetYear})`}
              value={d.suppressed ? INSUFFICIENT : d.value != null ? `${d.value.toFixed(1)}%` : NO_DATA}
            />
          ))}

          {FULL_PROFILE_UNIT_TYPES.includes(profile.unitType) && (
            <Button
              size="xs"
              variant='link'
              colorPalette="brand"
              alignSelf="flex-start"
              asChild><RouterLink to={`/dashboard/analytics/profile/${unitId}`}>Full profile →
                          </RouterLink></Button>
          )}

          {onAction && (
            <HStack pt={1} gap={2}>
              <Button size="xs" colorPalette="brand" onClick={() => onAction('create-turf', profile.boundaryWkt)}>
                Create turf
              </Button>
              <Button size="xs" variant="outline" onClick={() => onAction('export', profile.boundaryWkt)}>
                Export list
              </Button>
              <Button size="xs" variant="outline" onClick={() => onAction('apply-tags', profile.boundaryWkt)}>
                Apply tags
              </Button>
            </HStack>
          )}
        </VStack>
      )}
    </Box>
  );
}

function TurnoutSparkline({ points }: { points: TurnoutPoint[] }) {
  const usable = points.filter(p => p.value != null)
  if (usable.length < 2) return null
  const w = 96
  const h = 28
  const xs = usable.map((_, i) => (i / (usable.length - 1)) * (w - 4) + 2)
  const min = Math.min(...usable.map(p => p.value!))
  const max = Math.max(...usable.map(p => p.value!))
  const span = Math.max(max - min, 1)
  const ys = usable.map(p => h - 3 - ((p.value! - min) / span) * (h - 6))
  const path = xs.map((x, i) => `${x},${ys[i]}`).join(' ')
  // CSS vars resolve inside inline SVG in this app (precedent: react-icons
  // color="var(--chakra-colors-orange-500)"); this stroke/fill flips with dark mode.
  const stroke = vizSeqVar(400)
  return (
    <svg width={w} height={h} aria-hidden="true">
      <polyline points={path} fill="none" stroke={stroke} strokeWidth="1.5" />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r={1.75} fill={stroke} />
      ))}
    </svg>
  )
}
